import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { getArticleById, getRelatedArticles, getMostViewedArticles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { siteInfo } from "@/mocks/siteInfo";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const relatedArticles = article ? getRelatedArticles(article, 4) : [];
  const hotArticles = getMostViewedArticles(5);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 w-12 h-12 flex items-center justify-center mx-auto mb-4"></i>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors text-sm whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    publisher: {
      "@type": "Organization",
      name: siteInfo.name,
      url: siteInfo.url,
    },
    image: article.coverImage,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteInfo.url}/article/${article.id}`,
    },
    keywords: article.tags.join(", "),
    inLanguage: "zh-CN",
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `${article.title} - ${siteInfo.name}`,
    description: article.excerpt,
    url: `${siteInfo.url}/article/${article.id}`,
    isPartOf: {
      "@type": "WebSite",
      name: siteInfo.name,
      url: siteInfo.url,
    },
    about: {
      "@type": "Article",
      name: article.title,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>

      <Navbar />

      <main>
        <div className="pt-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
              <div className="flex-1 max-w-3xl">
                <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden mb-6">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={`每日黑料 - ${article.title}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                  {article.isHot && (
                    <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs rounded-full whitespace-nowrap">
                      热门
                    </span>
                  )}
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-6 border-b border-background-200/70">
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                    {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万阅读` : `${article.viewCount}阅读`}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-chat-3-line w-4 h-4 flex items-center justify-center"></i>
                    {article.commentCount >= 10000 ? `${(article.commentCount / 10000).toFixed(1)}万评论` : `${article.commentCount}评论`}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                    阅读约{article.readTime}分钟
                  </span>
                </div>

                <div
                  className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="mt-10 pt-8 border-t border-background-200/70">
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="text-sm text-foreground-500 whitespace-nowrap">分享到：</span>
                    <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微信">
                      <i className="ri-wechat-line text-foreground-700 w-4 h-4 flex items-center justify-center"></i>
                    </button>
                    <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微博">
                      <i className="ri-weibo-line text-foreground-700 w-4 h-4 flex items-center justify-center"></i>
                    </button>
                    <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer" aria-label="复制链接">
                      <i className="ri-link text-foreground-700 w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  </div>
                </div>
              </div>

              <aside className="lg:w-80 flex-shrink-0">
                <div className="sticky top-24 space-y-6">
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                    <h4 className="text-base font-heading font-bold text-foreground-900 mb-4">
                      <a href="#hot-articles-sidebar">热门爆料</a>
                    </h4>
                    <div className="flex flex-col gap-1">
                      {hotArticles.map((hot, index) => (
                        <Link
                          key={hot.id}
                          to={`/article/${hot.id}`}
                          className="flex items-start gap-3 p-2 rounded-lg hover:bg-background-200/70 transition-colors cursor-pointer group"
                        >
                          <span className="text-lg font-heading font-bold text-primary-300 group-hover:text-primary-500 transition-colors w-6 flex-shrink-0 text-center">
                            {index + 1}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm text-foreground-700 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                              {hot.title}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="bg-primary-50 rounded-xl p-5 border border-primary-200/50">
                    <h4 className="text-base font-heading font-bold text-foreground-900 mb-2">
                      <a href="#dasai-sidebar">每日黑料大赛</a>
                    </h4>
                    <p className="text-sm text-foreground-600 mb-3">第七期投票火热进行中，选出你心中的塌房之王！</p>
                    <Link
                      to="/category/meiri-dasai"
                      className="inline-block px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      立即投票
                    </Link>
                  </div>
                </div>
              </aside>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-14 pt-10 border-t border-background-200/70">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">相关爆料</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {relatedArticles.map((rel) => (
                    <ArticleCard key={rel.id} article={rel} variant="compact" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}